<!-- Copyright by the Spark Development Network; Licensed under the Rock Community License -->
<template>
    <li v-if="resource" :class="resourceCssClass">
        <img :src="imgSrc" class="avatar" draggable="false">
        <span ref="resourceNameElement"
              class="resource-name"
              data-toggle="tooltip"
              :data-original-title="resourceTooltip">
            {{ personName }}
            <i v-if="conflictTooltip" class="fa fa-exclamation-triangle"></i>
            <i v-if="preferenceTooltip" class="fa fa-minus-circle"></i>
        </span>
        <span class="resource-role">{{ resource?.GroupRoleName }}</span>

        <div v-no-drag-scroll class="dropdown">
            <button type="button"
                    class="btn btn-link btn-overflow"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    :disabled="isSchedulingDisabled">
                <i class="fa fa-ellipsis-v"></i>
            </button>

            <ul class="dropdown-menu dropdown-menu-right">
                <li>
                    <button type="button"
                            class="dropdown-item btn-link"
                            :disabled="isSchedulingDisabled"
                            @click="onUpdateResource(ResourceAction.MarkConfirmed)">
                        Mark Confirmed
                    </button>
                </li>
                <li>
                    <button type="button"
                            class="dropdown-item btn-link"
                            :disabled="isSchedulingDisabled"
                            @click="onUpdateResource(ResourceAction.MarkPending)">
                        Mark Pending
                    </button>
                </li>
                <li>
                    <button type="button"
                            class="dropdown-item btn-link"
                            :disabled="isSchedulingDisabled"
                            @click="onUpdateResource(ResourceAction.MarkDeclined)">
                        Mark Declined
                    </button>
                </li>
                <li>
                    <button type="button"
                            class="dropdown-item btn-link"
                            :disabled="isSchedulingDisabled"
                            @click="onUpdateResource(ResourceAction.ResendConfirmation)">
                        Resend Confirmation
                    </button>
                </li>
                <li v-if="groupMemberId">
                    <button type="button"
                            class="dropdown-item btn-link"
                            :disabled="isSchedulingDisabled"
                            @click="onUpdateResource(ResourceAction.UpdatePreference)">
                        Update Preference
                    </button>
                </li>
                <li>
                    <button type="button"
                            class="dropdown-item dropdown-item-danger btn-link"
                            :disabled="isSchedulingDisabled"
                            @click="onUpdateResource(ResourceAction.Remove)">
                        Remove
                    </button>
                </li>
            </ul>
        </div>

    </li>

    <UpdatePreference v-model:isVisible="isUpdatingPreference"
                      :occurrence="occurrence"
                      :resource="resource"
                      @preferenceUpdated="$emit('preferenceUpdated')"></UpdatePreference>
</template>

<script setup lang="ts">
    import { computed, PropType, ref, watch } from "vue";
    import { ISchedulerResourceAssignment, ISchedulerResourceAttend, Preference, ResourceAction } from "./types.partial";
    import UpdatePreference from "./updatePreference.partial.obs";
    import { NoDragScroll as vNoDragScroll } from "@Obsidian/Directives/dragScroll";
    import { useVModelPassthrough } from "@Obsidian/Utility/component";
    import { useHttp } from "@Obsidian/Utility/http";
    import { tooltip } from "@Obsidian/Utility/tooltip";
    import { GroupSchedulerOccurrenceBag } from "@Obsidian/ViewModels/Blocks/Group/Scheduling/GroupScheduler/groupSchedulerOccurrenceBag";

    const http = useHttp();

    const props = defineProps({
        occurrence: {
            type: Object as PropType<GroupSchedulerOccurrenceBag>,
            required: true
        },

        resource: {
            type: Object as PropType<ISchedulerResourceAttend>,
            required: true
        },

        updateResourceWarningMessage: {
            type: String as PropType<string>,
            default: ""
        },

        disabled: {
            type: Boolean as PropType<boolean>,
            default: false
        }
    });

    const emit = defineEmits<{
        (e: "resourceUpdated", action: ResourceAction): void,
        (e: "preferenceUpdated"): void
        (e: "update:updateResourceWarningMessage"): void
    }>();

    // #region Values

    const updateResourceWarningMessage = useVModelPassthrough(props, "updateResourceWarningMessage", emit);

    const isUpdatingResource = ref(false);
    const isUpdatingPreference = ref(false);

    const imgSrc = ref<string>(props.resource?.PersonPhotoUrl ?? "");
    const personName = ref<string>(props.resource?.PersonName ?? "");

    const resourceNameElement = ref<HTMLElement | null>(null);

    // #endregion

    // #region Computed Values

    const status = computed((): string => {
        return props.resource?.ConfirmationStatus ?? "";
    });

    const statusCssClass = computed((): string => {
        return `status-${(status.value ? `${status.value.toLowerCase()}` : "unknown")}`;
    });

    const resourceCssClass = computed((): string => {
        return `list-group-item text-sm ${statusCssClass.value}`;
    });

    const attendanceId = computed((): number => {
        return props.resource?.AttendanceId ?? 0;
    });

    const groupMemberId = computed((): number => {
        return props.resource?.GroupMemberId ?? 0;
    });

    const isSchedulingDisabled = computed((): boolean => {
        return props.disabled || isUpdatingResource.value;
    });

    const declinedReasonTooltip = computed((): string | null | undefined => {
        const reason = props.resource?.DeclinedReason;
        return reason
            ? wrapTooltipSection(`<div class='font-weight-semibold'>Declined Reason</div><div>${reason}</div>`)
            : "";
    });

    const preferenceList = computed((): ISchedulerResourceAssignment[] => {
        return props.resource
            ?.ResourcePreferenceList
            ?.filter((a: ISchedulerResourceAssignment) => a.ScheduleName) ?? [];
    });

    const preferenceTooltip = computed((): string => {
        let tooltip = "";

        if (props.resource?.MatchesPreference === Preference.notMatches
            && preferenceList.value.length) {
            tooltip += "<div class='font-weight-semibold'>Preference</div>";
            preferenceList.value.forEach((preference: ISchedulerResourceAssignment) => {
                const locationName = preference.LocationName ? ` - ${preference.LocationName}` : "";
                tooltip += `<div>${preference.ScheduleName}${locationName}</div>`;
            });
        }

        return tooltip
            ? wrapTooltipSection(tooltip)
            : "";
    });

    const schedulingConflictList = computed((): ISchedulerResourceAssignment[] => {
        return props.resource
            ?.SchedulingConflicts
            ?.filter((a: ISchedulerResourceAssignment) => a.ScheduleName) ?? [];
    });

    const conflictTooltip = computed((): string => {
        let tooltip = "";

        if (schedulingConflictList.value?.length) {
            schedulingConflictList.value.forEach((conflict: ISchedulerResourceAssignment) => {
                const locationName = conflict.LocationName ? ` - ${conflict.LocationName}` : "";
                tooltip += `<div>${conflict.ScheduleName}${locationName}</div>`;
            });
        }

        if (props.resource?.HasBlackoutConflict) {
            tooltip += "<div>Cannot be scheduled due to a blackout.</div>";
        }

        if (props.resource?.HasGroupRequirementsConflict) {
            tooltip += "<div>Does not meet the requirements for this group.</div>";
        }

        if (tooltip) {
            tooltip = `<div class='font-weight-semibold'>Scheduling Conflicts</div>${tooltip}`;
        }

        return tooltip
            ? wrapTooltipSection(tooltip)
            : "";
    });

    const resourceTooltip = computed((): string => {
        return `${declinedReasonTooltip.value}${preferenceTooltip.value}${conflictTooltip.value}`;
    });

    // #endregion

    // #region Functions

    /**
     * Returns a friendly action that can be used in messaging, based on the provided action.
     *
     * @param action The action for which to return a friendly action.
     */
    function getFriendlyAction(action: ResourceAction): string {
        switch (action) {
            case ResourceAction.MarkConfirmed:
                return `marking ${personName.value} as confirmed`;
            case ResourceAction.MarkPending:
                return `marking ${personName.value} as pending`;
            case ResourceAction.MarkDeclined:
                return `marking ${personName.value} as declined`;
            case ResourceAction.ResendConfirmation:
                return `resending confirmation to ${personName.value}`;
            case ResourceAction.UpdatePreference:
                return `updating preferences for ${personName.value}`;
            case ResourceAction.Remove:
                return `removing ${personName.value}`;
            default:
                return `updating ${personName.value}`;
        }
    }

    /**
     * Wraps a tooltip section with a div to make it easier to target for styling.
     *
     * @param tooltipSection The tooltip section to wrap.
     */
    function wrapTooltipSection(tooltipSection: string): string {
        return `<div class="tooltip-section">${tooltipSection}</div>`;
    }

    /**
     * Configures the tooltip.
     */
    function configureTooltip(): void {
        if (resourceNameElement.value) {
            tooltip(resourceNameElement.value, { html: true });
        }
    }

    // #endregion

    // #region Event Handlers

    /**
     * Updates the resource according to the specified action.
     *
     * @param action The update action to perform.
     */
    async function onUpdateResource(action: ResourceAction): Promise<void> {
        isUpdatingResource.value = true;
        updateResourceWarningMessage.value = "";

        const warningMessage = `There was an error while ${getFriendlyAction(action)}`;

        let endpoint = "";
        switch (action) {
            case ResourceAction.MarkConfirmed:
                endpoint = "ScheduledPersonConfirm";
                break;
            case ResourceAction.MarkPending:
                endpoint = "ScheduledPersonPending";
                break;
            case ResourceAction.MarkDeclined:
                endpoint = "ScheduledPersonDecline";
                break;
            case ResourceAction.ResendConfirmation:
                endpoint = "ScheduledPersonSendConfirmationCommunication";
                break;
            case ResourceAction.Remove:
                endpoint = "ScheduledPersonRemove";
                break;
            case ResourceAction.UpdatePreference:
                isUpdatingPreference.value = true;
                return;
            default:
                updateResourceWarningMessage.value = warningMessage;
                isUpdatingResource.value = false;
                return;
        }

        const result = await http.doApiCall("PUT", `/api/Attendances/${endpoint}?attendanceId=${attendanceId.value}`);
        isUpdatingResource.value = false;

        if (!result.isSuccess) {
            updateResourceWarningMessage.value = warningMessage;
            return;
        }

        emit("resourceUpdated", action);
    }

    // #endregion

    watch([
        resourceNameElement,
        resourceTooltip
    ], () => {
        configureTooltip();
    });

    watch(isUpdatingPreference, () => {
        if (!isUpdatingPreference.value) {
            isUpdatingResource.value = false;
        }
    });
</script>
